import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import {
  ItemModifierGroupUI,
  ItemModifierUI
} from '../../models/item-modifier-ui.model';
import { UiService } from '../../../../services/ui.service';
import { ItemDetailsStore } from '../../../../stores/item-details.store';
import { V2Color } from '../../../../v2-components/models/v2-colors.model';
import { LocationStore } from 'src/app/stores/location.store';
import { RxUtil } from 'utility/rx.util';
import { ModifierGroupService } from 'services/modifier-group.service';
import { filter, map } from 'rxjs/operators';
import { PlatformStore } from 'stores/platform.store';
import { unawaited } from 'utility/unawaited';
import { NestedModGroupSheetStore } from 'stores/nested-mod-group-sheet.store';
import { OrderMenuCartStore } from 'stores/order-menu-cart.store';
import { I18nMenuModifierBundle, I18nStore } from 'stores/i18n.store';
import { I18nPluralizableWord, I18nService } from 'services/i18n.service';

@Component({
  selector: 'v2-modifier-group',
  templateUrl: 'v2-modifier-group.component.html',
  styleUrls: ['v2-modifier-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class V2ModifierGroup implements OnInit, OnDestroy {
  @Input() parentModifier?: ItemModifierUI;
  @Input() modifierGroup!: ItemModifierGroupUI;
  @Input() showValidationState!: boolean;

  private defaultModifierSubscription?: Subscription;

  constructor(
    public uiService: UiService,
    public itemDetailsStore: ItemDetailsStore,
    public modifierGroupService: ModifierGroupService,
    public locationStore: LocationStore,
    public orderMenuCartStore: OrderMenuCartStore,
    private platformStore: PlatformStore,
    private nestedModGroupSheetStore: NestedModGroupSheetStore,
    public i18nStore: I18nStore,
    private i18nService: I18nService
  ) {}

  ngOnInit() {
    this.defaultModifierSubscription = combineLatest([
      this.locationStore.isMenuReadonly$,
      this.itemDetailsStore.modifierGroupsWithDefaultModifiersForSelectedItem$
    ])
      .pipe(
        filter(([isMenuReadonly, _]) => !isMenuReadonly),
        map(
          ([_, modifierGroupsWithDefaultModifiersForSelectedItem]) =>
            modifierGroupsWithDefaultModifiersForSelectedItem
        )
      )
      .subscribe((groupWithDefaultModifiers) => {
        const modifierGroup = groupWithDefaultModifiers.find(
          (group) =>
            group.ownershipChainKey === this.modifierGroup.ownershipChainKey
        );

        modifierGroup?.modifiers.forEach((modifier) => {
          this.itemDetailsStore.selectModifier(this.modifierGroup, {
            id: modifier.id,
            quantity: 1,
            maximumQty: modifier.maximumQty
          });
        });
      });
  }

  ngOnDestroy() {
    RxUtil.cleanupSubscription(this.defaultModifierSubscription);
  }

  getModifierGroupSubtitle(
    group: ItemModifierGroupUI,
    i18nBundle: I18nMenuModifierBundle | null
  ): string {
    if (!i18nBundle) return '';

    const min = group.minimum,
      max = group.maximum,
      modifiersLength = group.modifiers.length;

    if (!min && !max) {
      return i18nBundle.chooseYourOptions;
    } else if (
      min > 0 &&
      max > 0 &&
      min === modifiersLength &&
      max === modifiersLength
    ) {
      return `${
        i18nBundle.choose
      } ${modifiersLength} ${this.i18nService.pluralize(
        I18nPluralizableWord.Option,
        modifiersLength
      )}`;
    } else if (!min && max > 0) {
      return `${i18nBundle.chooseUpTo} ${max} ${this.i18nService.pluralize(
        I18nPluralizableWord.Option,
        max
      )}`;
    } else if (min > 0 && !max) {
      return `${i18nBundle.chooseAtLeast} ${min} ${this.i18nService.pluralize(
        I18nPluralizableWord.Option,
        min
      )}`;
    } else if (min > 0 && max > 0 && max !== min) {
      return `${i18nBundle.chooseAtLeast} ${min} ${i18nBundle.andUpTo} ${max}`;
    }

    return '';
  }

  getModifierTextColor(
    modifier: ItemModifierUI,
    defaultColor: V2Color
  ): V2Color {
    if (modifier.disabled) {
      return 'inactive-grey';
    }

    return modifier.isSelected ? 'darkest-grey' : defaultColor;
  }

  isIncrementQtyButtonDisabled(
    group: ItemModifierGroupUI,
    modifier: ItemModifierUI
  ): boolean {
    return (
      modifier.disabled ||
      this.itemDetailsStore.isModifierQuantityAtMaximum(group, {
        id: modifier.id,
        quantity: modifier.quantity,
        maximumQty: modifier.maximumQty
      })
    );
  }

  getIncrementQtyButtonColor(
    group: ItemModifierGroupUI,
    modifier: ItemModifierUI
  ): V2Color {
    return modifier.isSelected ||
      this.isIncrementQtyButtonDisabled(group, modifier)
      ? 'white'
      : 'primary';
  }

  selectModifier(group: ItemModifierGroupUI, modifier: ItemModifierUI) {
    this.itemDetailsStore.selectModifier(group, {
      id: modifier.id,
      quantity: 1,
      maximumQty: 1
    });

    this.handleNestedModifierSheetOpening(modifier);
  }

  private handleNestedModifierSheetOpening(modifier: ItemModifierUI) {
    if (modifier.isSelected && modifier.modifierGroups.length > 0) {
      if (!this.parentModifier) {
        this.nestedModGroupSheetStore.clearCurrentModifierGroupSheetParentModifierStack();
      }
      unawaited(
        this.modifierGroupService.openNestedModifierGroupSheet(modifier)
      );
    }
  }

  decrementModifierQuantity(
    event: Event,
    group: ItemModifierGroupUI,
    modifier: ItemModifierUI
  ) {
    event.stopPropagation();

    const quantity = modifier.quantity - 1;

    if (quantity >= 0) {
      this.itemDetailsStore.selectModifier(group, {
        id: modifier.id,
        quantity: quantity,
        maximumQty: modifier.maximumQty
      });
    }
  }

  incrementModifierQuantity(
    event: Event,
    group: ItemModifierGroupUI,
    modifier: ItemModifierUI
  ): void {
    event.stopPropagation();

    this.itemDetailsStore.selectModifier(group, {
      id: modifier.id,
      quantity: modifier.quantity + 1,
      maximumQty: modifier.maximumQty
    });
  }

  calculateTotalPrice(modifier: ItemModifierUI): number {
    return this.parentModifier
      ? modifier.price
      : this.orderMenuCartStore.totalModifierPrice(modifier);
  }

  calculateTotalMultiSelectPrice(modifier: ItemModifierUI): number {
    return this.parentModifier
      ? modifier.price
      : this.orderMenuCartStore.totalModifierGroups(modifier.modifierGroups) +
          modifier.price;
  }

  isModifierSelectable(modifier: ItemModifierUI): boolean {
    return modifier.inStock && !modifier.isUnavailableDueToAlcohol;
  }

  shouldShowNestedModifierSummary(modifier: ItemModifierUI): boolean {
    return (
      modifier.isSelected &&
      !this.parentModifier &&
      modifier.modifierGroups.length > 0
    );
  }

  readonly shouldShowRippleEffects$: Observable<boolean> = combineLatest([
    this.locationStore.isMenuReadonly$,
    this.platformStore.isAndroidOS$
  ]).pipe(
    map(([isMenuReadonly, isAndroidOS]) => !isMenuReadonly && !isAndroidOS)
  );

  onArrowClick(
    e: Event,
    group: ItemModifierGroupUI,
    modifier: ItemModifierUI
  ): void {
    e.stopPropagation();

    if (!modifier.isSelected) {
      return this.selectModifier(group, modifier);
    }

    this.handleNestedModifierSheetOpening(modifier);
  }
}
